'use client'

import { motion, useReducedMotion } from 'motion/react'
import { FastForward, Rewind } from 'lucide-react'
import styles from './preview-player-bar.module.scss'

type Props = {
  direction: -1 | 1
  expanded: boolean
  disabled?: boolean
  holding: boolean
  onBegin: (offset: number) => void
  onRelease: (offset: number) => void
  onCancel: () => void
  onScroll: (direction: -1 | 1) => void
}

export function PreviewPlayerJumpButton({
  direction,
  expanded,
  disabled,
  holding,
  onBegin,
  onRelease,
  onCancel,
  onScroll,
}: Props) {
  const reduceMotion = useReducedMotion()
  const offset = direction * 10
  const label = expanded
    ? direction < 0
      ? 'Back 10 assets'
      : 'Forward 10 assets'
    : direction < 0
      ? 'Previous page of assets'
      : 'Next page of assets'
  const Icon = direction < 0 ? Rewind : FastForward
  return (
    <motion.button
      layout="position"
      layoutDependency={expanded}
      layoutId={direction < 0 ? 'preview-player-rewind' : 'preview-player-forward'}
      whileHover={{ scale: reduceMotion ? 1 : 1.08 }}
      whileTap={{ scale: reduceMotion ? 1 : 0.88 }}
      transition={{
        duration: reduceMotion ? 0 : 0.2,
        ease: 'easeOut',
        layout: { duration: reduceMotion ? 0 : 0.38, ease: [0.22, 1, 0.36, 1] },
      }}
      type="button"
      className={styles.button}
      aria-label={label}
      title={expanded ? `${label} (Shift + ${direction < 0 ? '←' : '→'})` : label}
      aria-keyshortcuts={expanded ? (direction < 0 ? 'Shift+ArrowLeft' : 'Shift+ArrowRight') : undefined}
      disabled={disabled}
      onPointerDown={(event) => {
        if (expanded && event.button === 0) {
          event.currentTarget.setPointerCapture(event.pointerId)
          onBegin(offset)
        }
      }}
      onPointerUp={(event) => {
        if (expanded && event.button === 0) onRelease(offset)
      }}
      onPointerCancel={onCancel}
      onKeyDown={(event) => {
        if (expanded && [' ', 'Enter'].includes(event.key)) {
          event.preventDefault()
          if (!event.repeat) onBegin(offset)
        }
      }}
      onKeyUp={(event) => {
        if (expanded && [' ', 'Enter'].includes(event.key)) {
          event.preventDefault()
          onRelease(offset)
        }
      }}
      onBlur={() => {
        if (holding) onCancel()
      }}
      onClick={(event) => {
        if (!expanded) onScroll(direction)
        else if (event.detail === 0) onRelease(offset)
      }}
    >
      <Icon size={20} fill="currentColor" strokeWidth={0} />
    </motion.button>
  )
}
